import React, { useState, useMemo, useCallback } from 'react';
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import Select from 'react-select';
import { styled } from '@mui/material/styles';
import DropzoneArea from '../DropzoneArea';
import Constants from '../../../../assests/Constants/constants';

const BorderBoxButton = styled(Button)({
    boxSizing: 'border-box !important',
});

const isIncomeTransactionType = (transactionType) =>
    String(transactionType ?? '').trim().toUpperCase() === 'TR';

const formatRavginitTransactionType = (transactionType, t) => {
    const tt = String(transactionType ?? '').trim().toUpperCase();
    if (tt === 'TR') return t('RavginitIncome');
    if (tt === 'FA') return t('RavginitExpense');
    return transactionType || '';
};

const formatRavginitEntryKind = (entryKind, t) => {
    switch (String(entryKind ?? '').trim()) {
        case 'Invoice':
            return t('RavginitEntryKindInvoice');
        case 'Acceptance':
            return t('RavginitEntryKindAcceptance');
        case 'Payment':
            return t('RavginitEntryKindPayment');
        case 'Withholding':
            return t('RavginitWithholdingTax');
        default:
            return '';
    }
};

const snapVatPercentToOption = (vatPercent) => {
    const v = Number(vatPercent);
    if (!v || Number.isNaN(v) || v <= 0) return null;
    const options = Constants.VATPercentage ?? [];
    if (!options.length) return v;
    return options.reduce(
        (best, opt) => (Math.abs(opt - v) < Math.abs(best - v) ? opt : best),
        options[0]
    );
};

const vatPercentGroupKey = (vatPercent) => {
    const snapped = snapVatPercentToOption(vatPercent);
    if (snapped != null) return snapped;
    const v = Number(vatPercent);
    if (!Number.isNaN(v) && v > 0) return v;
    return 0;
};

const formatVatPercentLabel = (key) => {
    const n = Number(key);
    if (Number.isNaN(n) || n <= 0) return '0%';
    // Integers (100, 17, 0) must not use trailing-zero strip — "100".replace(/0+$/) → "1".
    if (Number.isInteger(n)) return `${n}%`;
    const trimmed = n.toFixed(3).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
    return `${trimmed}%`;
};

/** Per sortcode name (account description): income/expense counts + VAT % samples from journal lines. */
const buildSortCodeMetaFromEntries = (journalEntries) => {
    const map = new Map();
    (journalEntries ?? []).forEach((e) => {
        if (e.entryKind === 'Withholding') return;
        const name = String(e.accountDescription ?? '').trim();
        if (!name) return;
        if (!map.has(name)) {
            map.set(name, { incomeCount: 0, expenseCount: 0, vatPercents: [] });
        }
        const meta = map.get(name);
        if (isIncomeTransactionType(e.transactionType)) meta.incomeCount += 1;
        else meta.expenseCount += 1;
        const vp = Number(e.vatPercent);
        if (!Number.isNaN(vp) && vp > 0) meta.vatPercents.push(vp);
    });
    return map;
};

const inferSortCodeDefaults = (sortCodeName, sortCodeMeta) => {
    const meta = sortCodeMeta.get(String(sortCodeName ?? '').trim());
    if (!meta) return { sortCodeVatType: null, vat: null };

    const vatTransactions = String(Constants.SortCodeVatTypeValue?.VatTransactions ?? 1);
    const vatExpenses = String(Constants.SortCodeVatTypeValue?.VatExpenses ?? 2);
    const sortCodeVatType =
        meta.incomeCount > meta.expenseCount
            ? vatTransactions
            : meta.expenseCount > 0
              ? vatExpenses
              : vatExpenses;

    let vat = null;
    if (meta.vatPercents.length > 0) {
        const freq = new Map();
        meta.vatPercents.forEach((p) => {
            const snapped = snapVatPercentToOption(p);
            if (snapped != null) freq.set(snapped, (freq.get(snapped) ?? 0) + 1);
        });
        if (freq.size > 0) {
            vat = [...freq.entries()].sort((a, b) => b[1] - a[1])[0][0];
        }
    }

    return { sortCodeVatType, vat };
};

const normalizeRavginitMissingSortCode = (sc, sortCodeMeta) => {
    const sortCodeName = typeof sc === 'string'
        ? sc
        : (sc?.sortCodeName ?? sc?.SortCodeName ?? '');
    const idFromApi = typeof sc === 'string'
        ? ''
        : String(sc?.sortCodeId ?? sc?.SortCodeId ?? '').trim();
    const defaults = inferSortCodeDefaults(sortCodeName, sortCodeMeta);

    const existingVatType = sc?.sortCodeVatType ?? sc?.SortCodeVatType ?? null;
    const existingVat = typeof sc?.vat === 'number' ? sc.vat : typeof sc?.Vat === 'number' ? sc.Vat : null;
    const existingPar = sc?.participatesInProfitAndLoss ?? sc?.ParticipatesInProfitAndLoss;

    return {
        sortCodeId: /^\d+$/.test(idFromApi) ? idFromApi : (sortCodeName || idFromApi),
        sortCodeName: sortCodeName || idFromApi,
        sortCodeVatType:
            existingVatType !== null && existingVatType !== undefined && existingVatType !== ''
                ? String(existingVatType).trim()
                : defaults.sortCodeVatType,
        sortCodeCategory: sc?.sortCodeCategory ?? sc?.SortCodeCategory ?? null,
        participatesInProfitAndLoss:
            typeof existingPar === 'boolean' ? existingPar : true,
        vat: existingVat ?? defaults.vat,
    };
};

const RavginitJournalImportModal = ({
    open,
    onClose,
    previewRavginitJournal,
    commitRavginitJournal,
    createMissingSortCodes,
    direction,
    t,
    formattedSortCodeVatType = [],
    formattedSortCodesGroupNamesOptions = [],
    client,
    token,
    palCodes = [],
    accountCardOptions = [],
    fetchEnums,
    setLoading,
    user,
    setAccountCardOptions
}) => {
    const customStyles = {
        container: (provided) => ({
            ...provided,
            width: '100%',
            boxSizing: 'border-box !important'
        }),
        control: (provided) => ({
            ...provided,
            minHeight: '40px',
            width: '100% !important',
            borderRadius: '8px',
            background: '#F5F5F6',
            border: 'none',
            boxShadow: 'none',
            padding: '0 10px',
            textAlign: 'left',
            boxSizing: 'border-box !important',
            border: '1px solid transparent',
            '&:focus-within': {
                border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
            },
        }),
        placeholder: (provided) => ({
            ...provided,
            textAlign: 'left',
            color: 'black'
        }),
        singleValue: (provided) => ({
            ...provided,
            textAlign: 'left',
            color: 'black',
        }),
        menu: (provided) => ({
            ...provided,
            textAlign: 'left',
            color: 'black',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? '#F9AA2A'
                : state.isFocused
                    ? '#ebebeb'
                    : null,
            color: 'black',
        }),
        dropdownIndicator: (provided, state) => ({
            ...provided,
            color: state.isFocused ? 'darkgray' : 'black',
            '&:hover': {
                color: 'darkgray',
            },
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 2002,
        }),
    };

    const [journalFile, setJournalFile] = useState(null);
    const [previewDone, setPreviewDone] = useState(false);
    const [processing, setProcessing] = useState(false);

    const [entries, setEntries] = useState([]);
    const [missingAccountCards, setMissingAccountCards] = useState([]);
    const [editableMissing, setEditableMissing] = useState([]);
    const [parsedRows, setParsedRows] = useState(0);
    const [previewError, setPreviewError] = useState(null);

    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkVatType, setBulkVatType] = useState(null);
    const [bulkGroup, setBulkGroup] = useState(null);
    const [bulkPar, setBulkPar] = useState(null);
    const [bulkVat, setBulkVat] = useState(null);
    const [bulkFieldsChanged, setBulkFieldsChanged] = useState({
        vatType: false,
        group: false,
        par: false,
        vat: false
    });

    const [entriesPage, setEntriesPage] = useState(0);
    const [entriesRowsPerPage, setEntriesRowsPerPage] = useState(25);
    const [entriesOrderBy, setEntriesOrderBy] = useState('lineNumber');
    const [entriesOrder, setEntriesOrder] = useState('asc');
    const [entriesSearch, setEntriesSearch] = useState('');

    const isUnsupportedRavginitFile = (file) => {
        if (!file?.name) return false;
        const name = file.name.toLowerCase();
        return name.endsWith('.csv') || name.endsWith('.xlsx') || name.endsWith('.xls');
    };

    const handleJournalFileDrop = files => {
        if (!files?.length) return;
        const file = files[0];
        setJournalFile(file);
        setPreviewDone(false);
        setPreviewError(null);
        setEntriesPage(0);
        setEntriesSearch('');
        if (isUnsupportedRavginitFile(file)) {
            setPreviewError(t('RavginitWrongFileType'));
        }
    };

    const handlePreview = async () => {
        if (!journalFile) return;

        if (isUnsupportedRavginitFile(journalFile)) {
            setPreviewError(t('RavginitWrongFileType'));
            return;
        }

        try {
            setProcessing(true);
            setPreviewDone(false);
            setPreviewError(null);

            const res = await previewRavginitJournal(journalFile);

            const missingSortCodes = res?.missingSortCodes ?? [];
            const missingCards = res?.missingAccountCards ?? [];
            const journalEntries = res?.entries ?? [];
            const rows = res?.parsedJournalEntries ?? 0;

            if (rows === 0 && journalEntries.length === 0) {
                setPreviewError(t('RavginitPreviewEmpty'));
                setPreviewDone(false);
                return;
            }

            setEntries(journalEntries);
            setMissingAccountCards(missingCards);
            setParsedRows(rows);

            // Pre-fill missing sortcodes: Tr → מע"מ עיסקאות, Fa → מע"מ תשומות; VAT % from file lines.
            const sortCodeMeta = buildSortCodeMetaFromEntries(journalEntries);
            setEditableMissing(
                missingSortCodes.map((sc) => normalizeRavginitMissingSortCode(sc, sortCodeMeta))
            );

            setEntriesPage(0);
            setEntriesSearch('');
            setEntriesOrderBy('lineNumber');
            setEntriesOrder('asc');
            setPreviewDone(true);
        } catch (err) {
            console.error('Ravginit journal preview failed:', err);
            setPreviewDone(false);
            setPreviewError(err?.message || t('RavginitPreviewFailed'));
        } finally {
            setProcessing(false);
        }
    };

    const handleCreateAndCommit = async () => {
        if (!journalFile) return;

        try {
            setProcessing(true);

            if (editableMissing && editableMissing.length > 0) {
                await createMissingSortCodes(editableMissing);
            }

            const res = await commitRavginitJournal(journalFile);

            const errorCount = res?.errorCount ?? res?.[1] ?? 0;
            const errorMessages = res?.errorMessages ?? res?.[2] ?? [];

            if (errorCount > 0 && errorMessages.length > 0) {
                console.error('Import errors:', errorMessages);
            } else {
                handleClose();
            }
        } catch (err) {
            console.error('Ravginit journal commit error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const updateMissing = (index, field, value) =>
        setEditableMissing(prev => {
            const arr = [...prev];
            arr[index] = { ...arr[index], [field]: value };
            return arr;
        });

    const applyBulk = () => {
        setEditableMissing(prev =>
            prev.map(r =>
                selectedRows.includes(r.sortCodeId)
                    ? {
                        ...r,
                        sortCodeVatType: bulkFieldsChanged.vatType ? (bulkVatType?.value ?? null) : r.sortCodeVatType,
                        sortCodeCategory: bulkFieldsChanged.group ? (bulkGroup?.value ?? null) : r.sortCodeCategory,
                        participatesInProfitAndLoss: bulkFieldsChanged.par ? (bulkPar?.value ?? null) : r.participatesInProfitAndLoss,
                        vat: bulkFieldsChanged.vat ? (bulkVat?.value ?? null) : r.vat,
                    }
                    : r
            )
        );
        setSelectedRows([]);
        setBulkVatType(null);
        setBulkGroup(null);
        setBulkPar(null);
        setBulkVat(null);
        setBulkFieldsChanged({ vatType: false, group: false, par: false, vat: false });
    };

    const missingIncomplete = useMemo(
        () => editableMissing.some(sc =>
            !sc.sortCodeName ||
            !sc.sortCodeName.trim() ||
            sc.sortCodeVatType === null ||
            sc.sortCodeVatType === undefined ||
            sc.participatesInProfitAndLoss === null ||
            sc.participatesInProfitAndLoss === undefined ||
            sc.vat === null ||
            sc.vat === undefined
        ),
        [editableMissing]
    );

    const previewSummaries = useMemo(() => {
        const totalEntries = entries.length;
        const documentEntries = entries.filter((e) => e.entryKind !== 'Withholding');
        const incomeCount = documentEntries.filter((e) => isIncomeTransactionType(e.transactionType)).length;
        const expenseCount = documentEntries.length - incomeCount;
        const uniqueSuppliers = new Set(
            entries.map((e) => String(e.taxId ?? '').trim()).filter((id) => id && id !== '000000000')
        ).size;
        const uniqueSortCodes = new Set(
            documentEntries.map((e) => String(e.accountDescription ?? '').trim()).filter(Boolean)
        ).size;

        const vatGroupMap = new Map();
        let totalGross = 0;
        let totalNet = 0;
        let totalVat = 0;

        // Withholding entries are a movement of part of the document amount, so they stay
        // out of the VAT breakdown to keep it tied to the documents themselves.
        entries.forEach((e) => {
            if (e.entryKind === 'Withholding') return;

            const gross = Number(e.amount) || 0;
            const vat = Number(e.vatAmount) || 0;
            const net = gross - vat;
            const key = vatPercentGroupKey(e.vatPercent);

            if (!vatGroupMap.has(key)) {
                vatGroupMap.set(key, { vatPercentKey: key, count: 0, gross: 0, net: 0, vat: 0 });
            }
            const row = vatGroupMap.get(key);
            row.count += 1;
            row.gross += gross;
            row.net += net;
            row.vat += vat;

            totalGross += gross;
            totalNet += net;
            totalVat += vat;
        });

        const vatBreakdown = [...vatGroupMap.values()].sort(
            (a, b) => Number(b.vatPercentKey) - Number(a.vatPercentKey)
        );

        const invoiceCount = entries.filter((e) => e.entryKind === 'Invoice').length;
        const acceptanceCount = entries.filter((e) => e.entryKind === 'Acceptance').length;
        const paymentCount = entries.filter((e) => e.entryKind === 'Payment').length;
        const withholdingEntries = entries.filter((e) => e.entryKind === 'Withholding');
        const withholdingCount = withholdingEntries.length;
        const totalWithholding = withholdingEntries.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);

        return {
            totalEntries,
            incomeCount,
            expenseCount,
            invoiceCount,
            acceptanceCount,
            paymentCount,
            withholdingCount,
            totalWithholding,
            sumAmount: totalGross,
            sumVatAmount: totalVat,
            totalGross,
            totalNet,
            totalVat,
            vatBreakdown,
            uniqueSuppliers,
            uniqueSortCodes,
            missingSortCodes: editableMissing.length,
            cardsToAutoCreate: missingAccountCards.length,
        };
    }, [entries, editableMissing.length, missingAccountCards.length]);

    const formatDate = (iso) => {
        if (!iso) return '';
        try {
            const d = new Date(iso);
            if (isNaN(d.getTime())) return '';
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yy = String(d.getFullYear()).slice(-2);
            return `${dd}/${mm}/${yy}`;
        } catch {
            return '';
        }
    };

    const formatAmount = (n) => {
        if (n === null || n === undefined) return '';
        const num = typeof n === 'number' ? n : parseFloat(n);
        if (isNaN(num)) return '';
        return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const entrySearchHaystack = useCallback((e) => {
        const debitId = e.debitAccount ?? '';
        const creditId = e.creditAccount ?? '';
        const debitName = e.debitName ?? e.debitAccountName ?? '';
        const creditName = e.creditName ?? e.creditAccountName ?? '';
        return [
            e.lineNumber,
            formatDate(e.documentDate),
            e.sequence,
            e.supplierName,
            e.taxId,
            debitId,
            debitName,
            creditId,
            creditName,
            e.amount,
            e.vatPercent,
            e.vatAmount,
            e.accountDescription,
            e.withholdingTaxAmount,
            formatRavginitTransactionType(e.transactionType, t),
            formatRavginitEntryKind(e.entryKind, t),
        ]
            .filter((v) => v !== null && v !== undefined)
            .join(' ')
            .toLowerCase();
    }, [t]);

    const filteredEntries = useMemo(() => {
        const q = entriesSearch.trim().toLowerCase();
        if (!q) return entries;
        return entries.filter((e) => entrySearchHaystack(e).includes(q));
    }, [entries, entriesSearch, entrySearchHaystack]);

    const getEntryComparator = useCallback((order, orderBy) => (a, b) => {
        const aVal = a[orderBy];
        const bVal = b[orderBy];
        const isNum = (v) => v != null && v !== '' && !Number.isNaN(Number(v));
        if (order === 'asc') {
            if (isNum(aVal) && isNum(bVal)) return Number(aVal) - Number(bVal);
            return String(aVal ?? '').localeCompare(String(bVal ?? ''), undefined, { numeric: true });
        }
        if (isNum(aVal) && isNum(bVal)) return Number(bVal) - Number(aVal);
        return String(bVal ?? '').localeCompare(String(aVal ?? ''), undefined, { numeric: true });
    }, []);

    const sortedEntries = useMemo(() => {
        if (!filteredEntries.length) return [];
        return [...filteredEntries].sort(getEntryComparator(entriesOrder, entriesOrderBy));
    }, [filteredEntries, entriesOrder, entriesOrderBy, getEntryComparator]);

    const paginatedEntries = useMemo(
        () => sortedEntries.slice(
            entriesPage * entriesRowsPerPage,
            entriesPage * entriesRowsPerPage + entriesRowsPerPage
        ),
        [sortedEntries, entriesPage, entriesRowsPerPage]
    );

    const handleEntriesSort = (property) => {
        const isAsc = entriesOrderBy === property && entriesOrder === 'asc';
        setEntriesOrder(isAsc ? 'desc' : 'asc');
        setEntriesOrderBy(property);
        setEntriesPage(0);
    };

    const renderEntriesSortHeader = (id, label, align = 'left') => (
        <TableCell align={align} sx={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>
            <TableSortLabel
                active={entriesOrderBy === id}
                direction={entriesOrderBy === id ? entriesOrder : 'asc'}
                onClick={() => handleEntriesSort(id)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    const handleClose = () => {
        setJournalFile(null);
        setPreviewDone(false);
        setEntries([]);
        setMissingAccountCards([]);
        setEditableMissing([]);
        setParsedRows(0);
        setSelectedRows([]);
        setPreviewError(null);
        setEntriesPage(0);
        setEntriesSearch('');
        onClose();
    };

    if (!open) return null;

    return (
        <div className="download-modal-overlay" onClick={handleClose} style={{ zIndex: 2000 }}>
            <div className="download-modal" onClick={e => e.stopPropagation()} style={{ zIndex: 2001 }}>
                <div className={`download-modal-content ${direction}`} style={{ position: 'relative', maxWidth: previewDone ? '1400px' : '600px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h4 className="report-title">
                            {t('ImportRavginitJournalEntries')}
                        </h4>
                        <IconButton
                            className="client-back-icon"
                            onClick={handleClose}
                            sx={{ padding: '4px' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                        {t('RavginitFileFormat')}
                    </p>
                    <p style={{ fontSize: '12px', color: '#888', marginBottom: '15px', textAlign: direction === 'rtl' ? 'right' : 'left', lineHeight: 1.5 }}>
                        {t('RavginitFileFormatHelp')}
                    </p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div>
                            <h5>{t('JournalEntriesFile')}</h5>
                            <DropzoneArea
                                key={journalFile ? journalFile.name : 'ravginit-journal-empty'}
                                onDrop={handleJournalFileDrop}
                                filesLimit={1}
                            />
                            {journalFile && (
                                <div style={{ marginTop: 8 }}>
                                    <span>{t('FileSelected')}: {journalFile.name}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {previewError && (
                        <div style={{ marginTop: 16, padding: 12, backgroundColor: '#fdecea', borderRadius: 6, border: '1px solid #f44336', color: '#b71c1c', fontSize: '14px', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                            {previewError}
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: 15, marginTop: 20 }}>
                        <Button
                            variant="contained"
                            onClick={handlePreview}
                            disabled={!journalFile || processing}
                        >
                            {t('Preview')}
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            disabled={!previewDone || processing || missingIncomplete}
                            onClick={handleCreateAndCommit}
                        >
                            {processing ? t('Processing') : t('CreateAndImport')}
                        </Button>
                    </div>

                    {previewDone && (
                        <div style={{ marginTop: 25, maxHeight: 800, overflow: 'auto' }}>
                            <div
                                style={{
                                    marginBottom: 15,
                                    padding: 12,
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: 6,
                                    border: '1px solid #e0e0e0',
                                    textAlign: direction === 'rtl' ? 'right' : 'left',
                                }}
                            >
                                <p style={{ fontSize: '14px', color: '#444', margin: '0 0 8px' }}>
                                    {t('TotalLines')}: <strong>{parsedRows || previewSummaries.totalEntries}</strong>
                                    {' | '}
                                    {t('ValidCount')}: <strong>{previewSummaries.totalEntries}</strong>
                                </p>
                                <p style={{ fontSize: '14px', color: '#444', margin: '0 0 8px' }}>
                                    {t('RavginitIncomeCount')}: <strong>{previewSummaries.incomeCount}</strong>
                                    {' | '}
                                    {t('RavginitExpenseCount')}: <strong>{previewSummaries.expenseCount}</strong>
                                    {' | '}
                                    {t('RavginitUniqueSuppliers')}: <strong>{previewSummaries.uniqueSuppliers}</strong>
                                    {' | '}
                                    {t('SortCode Name')}: <strong>{previewSummaries.uniqueSortCodes}</strong>
                                </p>
                                <p style={{ fontSize: '14px', color: '#444', margin: '0 0 8px' }}>
                                    {t('RavginitEntryKindInvoice')}: <strong>{previewSummaries.invoiceCount}</strong>
                                    {' | '}
                                    {t('RavginitEntryKindAcceptance')}: <strong>{previewSummaries.acceptanceCount}</strong>
                                    {' | '}
                                    {t('RavginitEntryKindPayment')}: <strong>{previewSummaries.paymentCount}</strong>
                                    {previewSummaries.withholdingCount > 0 && (
                                        <>
                                            {' | '}
                                            {t('RavginitWithholdingTax')}:{' '}
                                            <strong>
                                                {previewSummaries.withholdingCount}
                                                {' · '}
                                                {formatAmount(previewSummaries.totalWithholding)}
                                            </strong>
                                        </>
                                    )}
                                </p>
                                <p style={{ fontSize: '14px', color: '#666', margin: '0 0 12px' }}>
                                    {t('MissingSortCodes')}: <strong>{previewSummaries.missingSortCodes}</strong>
                                    {' | '}
                                    {t('CardsToAutoCreate')}: <strong>{previewSummaries.cardsToAutoCreate}</strong>
                                </p>

                                {previewSummaries.vatBreakdown.length > 0 && (
                                    <>
                                        <h6 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: '#333' }}>
                                            {t('RavginitSummaryByVat')}
                                        </h6>
                                        <table
                                            className="sortcode-table"
                                            style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8, fontSize: 13 }}
                                        >
                                            <thead>
                                                <tr style={{ backgroundColor: '#e8e8e8' }}>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                        {t('VATPercent')}
                                                    </th>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                        {t('Quantity')}
                                                    </th>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                                                        {t('MoveinNet')}
                                                    </th>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                                                        {t('MoveinVat')}
                                                    </th>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                                                        {t('MoveinGross')}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewSummaries.vatBreakdown.map((row) => (
                                                    <tr key={String(row.vatPercentKey)}>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                            {formatVatPercentLabel(row.vatPercentKey)}
                                                        </td>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                            {row.count}
                                                        </td>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'right' }}>
                                                            {formatAmount(row.net)}
                                                        </td>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'right' }}>
                                                            {formatAmount(row.vat)}
                                                        </td>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'right' }}>
                                                            {formatAmount(row.gross)}
                                                        </td>
                                                    </tr>
                                                ))}
                                                <tr style={{ backgroundColor: '#e3f2fd', fontWeight: 700 }}>
                                                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                        {t('Total')}
                                                    </td>
                                                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                        {previewSummaries.totalEntries}
                                                    </td>
                                                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                                                        {formatAmount(previewSummaries.totalNet)}
                                                    </td>
                                                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                                                        {formatAmount(previewSummaries.totalVat)}
                                                    </td>
                                                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                                                        {formatAmount(previewSummaries.totalGross)}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </>
                                )}
                            </div>

                            {/* Missing Sortcodes Validation Table */}
                            {editableMissing.length === 0 ? (
                                <p style={{ color: 'green' }}>{t('NoMissingSortCodes') || 'אין סוגי תנועות חסרים'}</p>
                            ) : (
                                <>
                                    <h5>{t('MissingSortCodes') || 'סוגי תנועות חסרים'}</h5>
                                    {/* Bulk Edit Controls */}
                                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                                        <Select
                                            placeholder={t('SelectSortCodeVatType')}
                                            options={formattedSortCodeVatType}
                                            value={bulkVatType}
                                            onChange={(val) => {
                                                setBulkVatType(val);
                                                setBulkFieldsChanged(prev => ({ ...prev, vatType: true }));
                                            }}
                                            styles={{ ...customStyles, control: (provided) => ({ ...customStyles.control(provided) }) }}
                                            isClearable={true}
                                        />
                                        <Select
                                            placeholder={t('SelectSortCodeGroupName')}
                                            options={formattedSortCodesGroupNamesOptions}
                                            value={bulkGroup}
                                            onChange={(val) => {
                                                setBulkGroup(val);
                                                setBulkFieldsChanged(prev => ({ ...prev, group: true }));
                                            }}
                                            styles={customStyles}
                                            isClearable={true}
                                        />
                                        <Select
                                            placeholder={t('participatesInProfitAndLoss')}
                                            options={[{ label: t('Yes'), value: true }, { label: t('No'), value: false }]}
                                            value={bulkPar}
                                            onChange={(val) => {
                                                setBulkPar(val);
                                                setBulkFieldsChanged(prev => ({ ...prev, par: true }));
                                            }}
                                            isSearchable={false}
                                            styles={customStyles}
                                            isClearable={true}
                                        />
                                        <Select
                                            placeholder={t('VAT')}
                                            options={Constants.VATPercentage.map(v => ({ label: `${v}%`, value: v }))}
                                            value={bulkVat}
                                            onChange={(val) => {
                                                setBulkVat(val);
                                                setBulkFieldsChanged(prev => ({ ...prev, vat: true }));
                                            }}
                                            isSearchable={false}
                                            styles={customStyles}
                                            isClearable={true}
                                        />
                                        <BorderBoxButton
                                            variant="outlined"
                                            disabled={!bulkFieldsChanged.vatType && !bulkFieldsChanged.group && !bulkFieldsChanged.par && !bulkFieldsChanged.vat}
                                            onClick={applyBulk}
                                        >
                                            {t('ApplyToSelected')}
                                        </BorderBoxButton>
                                    </div>

                                    <table className="sortcode-table editable">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '5%' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedRows.length === editableMissing.length && editableMissing.length > 0}
                                                        onChange={e => setSelectedRows(e.target.checked ? editableMissing.map(r => r.sortCodeId) : [])}
                                                    />
                                                </th>
                                                <th>{t('SortCode Id')}</th>
                                                <th>{t('SortCode Name')}</th>
                                                <th>{t('SelectSortCodeVatType')}</th>
                                                <th>{t('SelectSortCodeGroupName')}</th>
                                                <th>{t('participatesInProfitAndLoss')}</th>
                                                <th>{t('VAT')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {editableMissing.map((sc, i) => (
                                                <tr key={sc.sortCodeId}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedRows.includes(sc.sortCodeId)}
                                                            onChange={e => setSelectedRows(prev => e.target.checked ? [...prev, sc.sortCodeId] : prev.filter(id => id !== sc.sortCodeId))}
                                                        />
                                                    </td>
                                                    <td>
                                                        <TextField
                                                            fullWidth
                                                            value={sc.sortCodeId}
                                                            variant="standard"
                                                            InputProps={{
                                                                readOnly: true,
                                                                disableUnderline: true,
                                                                sx: {
                                                                    height: '48px',
                                                                    padding: '0 10px',
                                                                    borderRadius: '8px',
                                                                    background: '#F5F5F6',
                                                                    border: '1px solid transparent'
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <TextField
                                                            fullWidth
                                                            value={sc.sortCodeName || ''}
                                                            variant="standard"
                                                            onChange={e => updateMissing(i, 'sortCodeName', e.target.value)}
                                                            placeholder={t('EnterSortCodeName') || 'Enter sortcode name'}
                                                            InputProps={{
                                                                disableUnderline: true,
                                                                sx: {
                                                                    height: '48px',
                                                                    padding: '0 10px',
                                                                    borderRadius: '8px',
                                                                    background: (!sc.sortCodeName || !sc.sortCodeName.trim()) ? 'white' : '#F5F5F6',
                                                                    border: (!sc.sortCodeName || !sc.sortCodeName.trim()) ? '1.5px solid #f44336' : '1px solid transparent',
                                                                },
                                                            }}
                                                            inputProps={{
                                                                style: {
                                                                    textAlign: direction === 'ltr' ? 'left' : 'right',
                                                                },
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Select
                                                            placeholder={t('SelectSortCodeVatType')}
                                                            options={formattedSortCodeVatType}
                                                            value={formattedSortCodeVatType.find(opt => opt.value === sc.sortCodeVatType?.toString()?.trim()) || null}
                                                            onChange={opt => updateMissing(i, 'sortCodeVatType', opt.value)}
                                                            styles={{
                                                                ...customStyles, control: (provided) => ({
                                                                    ...customStyles.control(provided),
                                                                    border: (sc.sortCodeVatType !== null && sc.sortCodeVatType !== undefined)
                                                                        ? '1px solid transparent'
                                                                        : '1.5px solid #f44336'
                                                                })
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Select
                                                            placeholder={t('SelectSortCodeGroupName')}
                                                            options={formattedSortCodesGroupNamesOptions}
                                                            value={formattedSortCodesGroupNamesOptions.find(opt => opt.value === sc.sortCodeCategory) || null}
                                                            onChange={opt => updateMissing(i, 'sortCodeCategory', opt ? opt.value : null)}
                                                            styles={customStyles}
                                                            isClearable={true}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Select
                                                            placeholder={t('participatesInProfitAndLoss')}
                                                            options={[{ label: t('Yes'), value: true }, { label: t('No'), value: false }]}
                                                            value={sc.participatesInProfitAndLoss === true ? { label: t('Yes'), value: true } : sc.participatesInProfitAndLoss === false ? { label: t('No'), value: false } : null}
                                                            onChange={opt => updateMissing(i, 'participatesInProfitAndLoss', opt.value)}
                                                            styles={{ ...customStyles, control: provided => ({ ...customStyles.control(provided), border: sc.participatesInProfitAndLoss !== undefined && sc.participatesInProfitAndLoss !== null ? '1px solid transparent' : '1.5px solid #f44336' }) }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Select
                                                            placeholder={t('VAT')}
                                                            options={Constants.VATPercentage.map(v => ({ label: `${v}%`, value: v }))}
                                                            value={Constants.VATPercentage.map(v => ({ label: `${v}%`, value: v })).find(opt => opt.value === sc.vat) || null}
                                                            onChange={opt => updateMissing(i, 'vat', opt.value)}
                                                            styles={{ ...customStyles, control: provided => ({ ...customStyles.control(provided), border: sc.vat !== undefined && sc.vat !== null ? '1px solid transparent' : '1.5px solid #f44336' }) }}
                                                            isSearchable={false}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            )}

                            {/* Cards to be auto-created on commit (informational only — not a blocker). */}
                            {missingAccountCards.length > 0 && (
                                <div style={{ marginTop: 25, padding: 12, backgroundColor: '#e8f4fd', border: '1px solid #2196f3', borderRadius: 6 }}>
                                    <h5 style={{ marginTop: 0, marginBottom: 8, color: '#0d47a1' }}>
                                        {t('CardsToAutoCreate') || 'כרטיסים שייווצרו אוטומטית בעת הייבוא'} ({missingAccountCards.length})
                                    </h5>
                                    <p style={{ fontSize: 12, color: '#1565c0', marginBottom: 8 }}>
                                        {t('CardsToAutoCreateHelp')}
                                    </p>
                                    <p style={{ fontSize: 12, color: '#1565c0', marginBottom: 8 }}>
                                        {t('RavginitJournalIncludesCardsHelp')}
                                    </p>
                                    <table className="sortcode-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#bbdefb' }}>
                                                <th style={{ padding: '6px 8px', border: '1px solid #90caf9', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('CardName')}</th>
                                                <th style={{ padding: '6px 8px', border: '1px solid #90caf9' }}>{t('TaxId')}</th>
                                                <th style={{ padding: '6px 8px', border: '1px solid #90caf9', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('PalCode')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {missingAccountCards.map((card, idx) => {
                                                const name = typeof card === 'string'
                                                    ? card
                                                    : (card.cardName || card.CardName || '');
                                                const id = typeof card === 'string'
                                                    ? ''
                                                    : (card.cardId || card.CardId || '');
                                                const palCode = typeof card === 'string'
                                                    ? ''
                                                    : (card.palCode || card.PalCode || '');
                                                return (
                                                    <tr key={idx}>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #90caf9', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{name || id}</td>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #90caf9' }}>{id}</td>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #90caf9', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{palCode}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Journal Entries Preview — MUI table with search & pagination */}
                            {entries.length > 0 && (
                                <Box mt={3}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            {t('JournalEntries')} ({filteredEntries.length}{entriesSearch.trim() ? ` / ${entries.length}` : ''})
                                        </Typography>
                                        <TextField
                                            size="small"
                                            placeholder={t('Search')}
                                            value={entriesSearch}
                                            onChange={(ev) => {
                                                setEntriesSearch(ev.target.value);
                                                setEntriesPage(0);
                                            }}
                                            InputProps={{
                                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active', fontSize: 20 }} />,
                                            }}
                                            sx={{ minWidth: 220 }}
                                        />
                                    </Box>
                                    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    {renderEntriesSortHeader('lineNumber', '#')}
                                                    {renderEntriesSortHeader('documentDate', t('DocumentDate'))}
                                                    {renderEntriesSortHeader('sequence', t('Reference'))}
                                                    {renderEntriesSortHeader('supplierName', t('RavginitSupplierOrCustomerName'))}
                                                    {renderEntriesSortHeader('taxId', t('TaxId'))}
                                                    {renderEntriesSortHeader('debitAccount', t('DebitAccount'))}
                                                    {renderEntriesSortHeader('debitName', t('DebitName'))}
                                                    {renderEntriesSortHeader('creditAccount', t('CreditAccount'))}
                                                    {renderEntriesSortHeader('creditName', t('CreditName'))}
                                                    {renderEntriesSortHeader('amount', t('Amount'), 'right')}
                                                    {renderEntriesSortHeader('vatPercent', t('VATPercent'), 'right')}
                                                    {renderEntriesSortHeader('vatAmount', t('VATAmount'), 'right')}
                                                    {renderEntriesSortHeader('withholdingTaxAmount', t('RavginitWithholdingTax'), 'right')}
                                                    {renderEntriesSortHeader('transactionType', t('RavginitTransactionKind'))}
                                                    {renderEntriesSortHeader('entryKind', t('RavginitEntryKind'))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {paginatedEntries.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={15} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                                            {t('NoResults') || t('Search')}
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    paginatedEntries.map((e) => {
                                                        const debitId = e.debitAccount;
                                                        const creditId = e.creditAccount;
                                                        const debitName = e.debitName ?? e.debitAccountName ?? '';
                                                        const creditName = e.creditName ?? e.creditAccountName ?? '';
                                                        const vatPercent = (e.vatPercent !== null && e.vatPercent !== undefined)
                                                            ? e.vatPercent
                                                            : null;
                                                        return (
                                                            <TableRow
                                                                key={`${e.lineNumber ?? e.sequence}-${e.entryKind ?? ''}-${e.amount}`}
                                                                hover
                                                            >
                                                                <TableCell sx={{ fontSize: 12 }}>{e.lineNumber}</TableCell>
                                                                <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDate(e.documentDate)}</TableCell>
                                                                <TableCell sx={{ fontSize: 12 }}>{e.sequence || ''}</TableCell>
                                                                <TableCell sx={{ fontSize: 12, maxWidth: 160, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                    {e.supplierName || ''}
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: 12 }}>{e.taxId || ''}</TableCell>
                                                                <TableCell sx={{ fontSize: 12 }}>
                                                                    {debitId ? debitId : (
                                                                        <Typography component="span" variant="body2" color="primary">
                                                                            {t('Auto')}
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: 12, maxWidth: 140, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                    {debitName}
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: 12 }}>
                                                                    {creditId ? creditId : (
                                                                        <Typography component="span" variant="body2" color="primary">
                                                                            {t('Auto')}
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: 12, maxWidth: 140, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                    {creditName}
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ fontSize: 12 }}>{formatAmount(e.amount)}</TableCell>
                                                                <TableCell align="right" sx={{ fontSize: 12 }}>
                                                                    {vatPercent !== null ? `${formatAmount(vatPercent)}%` : ''}
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ fontSize: 12 }}>{formatAmount(e.vatAmount)}</TableCell>
                                                                <TableCell align="right" sx={{ fontSize: 12 }}>
                                                                    {Number(e.withholdingTaxAmount) > 0 ? formatAmount(e.withholdingTaxAmount) : ''}
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                                                    {formatRavginitTransactionType(e.transactionType, t)}
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                                                    {formatRavginitEntryKind(e.entryKind, t)}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })
                                                )}
                                            </TableBody>
                                        </Table>
                                        <TablePagination
                                            component="div"
                                            count={sortedEntries.length}
                                            page={entriesPage}
                                            onPageChange={(_, p) => setEntriesPage(p)}
                                            rowsPerPage={entriesRowsPerPage}
                                            onRowsPerPageChange={(e) => {
                                                setEntriesRowsPerPage(parseInt(e.target.value, 10));
                                                setEntriesPage(0);
                                            }}
                                            rowsPerPageOptions={[10, 25, 50, 100]}
                                            labelRowsPerPage={t('RowsPerPage')}
                                            SelectProps={{
                                                MenuProps: {
                                                    // Modal overlay sits at z-index 2000-2002; lift the
                                                    // rows-per-page dropdown above it so it isn't hidden.
                                                    sx: { zIndex: 2100 },
                                                },
                                            }}
                                        />
                                    </TableContainer>
                                </Box>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RavginitJournalImportModal;
